import { FocusTrap, Portal } from "@headlessui/react";
import { useEffect, useId, useRef } from "react";

// The nested delete confirmation is portalled outside this dialog by Headless UI.
export const REVIEW_DELETE_DIALOG_CLASS = "annual-client-review-delete-dialog";

const ScrollableReviewDialogContent = ({ children, onClose }) => {
    const dialogRef = useRef(null);
    const titleId = useId();
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    const nestedDialogs = () =>
        document.querySelectorAll(`.${REVIEW_DELETE_DIALOG_CLASS}`);

    useEffect(() => {
        const dialog = dialogRef.current;
        const isReviewTarget = (target) =>
            target instanceof Node &&
            (dialog.contains(target) ||
                Array.from(nestedDialogs()).some((nested) => nested.contains(target)));

        // Let native wheel and touch scrolling reach the original scroll container.
        // Block activation in capture phase, before React handlers or browser defaults.
        const blockBackgroundAction = (event) => {
            if (isReviewTarget(event.target)) return;
            event.preventDefault();
            event.stopPropagation();
            if (event.type === "click") onCloseRef.current();
        };
        const stopBackgroundTouch = (event) => {
            if (!isReviewTarget(event.target)) event.stopPropagation();
        };
        const handleKeyDown = (event) => {
            if (event.key === "Escape" && nestedDialogs().length === 0) {
                event.preventDefault();
                event.stopPropagation();
                onCloseRef.current();
            } else {
                blockBackgroundAction(event);
            }
        };
        const blockedEvents = [
            "pointerdown", "pointerup", "mousedown", "mouseup", "click",
            "dblclick", "auxclick", "contextmenu", "dragstart", "submit",
            "beforeinput", "input", "change", "keyup",
        ];
        blockedEvents.forEach((type) =>
            document.addEventListener(type, blockBackgroundAction, true),
        );
        document.addEventListener("keydown", handleKeyDown, true);
        document.addEventListener("touchstart", stopBackgroundTouch, { capture: true, passive: true });
        document.addEventListener("touchend", stopBackgroundTouch, { capture: true, passive: true });

        // Unlike inert, aria-hidden does not disable native scrolling of the page.
        const background = Array.from(document.body.children)
            .filter((element) => !element.contains(dialog))
            .map((element) => [element, element.getAttribute("aria-hidden")]);
        background.forEach(([element]) => element.setAttribute("aria-hidden", "true"));

        return () => {
            blockedEvents.forEach((type) =>
                document.removeEventListener(type, blockBackgroundAction, true),
            );
            document.removeEventListener("keydown", handleKeyDown, true);
            document.removeEventListener("touchstart", stopBackgroundTouch, true);
            document.removeEventListener("touchend", stopBackgroundTouch, true);
            background.forEach(([element, previous]) => {
                if (previous === null) element.removeAttribute("aria-hidden");
                else element.setAttribute("aria-hidden", previous);
            });
        };
    }, []);

    return (
        <FocusTrap
            ref={dialogRef}
            containers={nestedDialogs}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            className="pointer-events-none fixed inset-0 z-[200]"
        >
            <div className="fixed inset-0 bg-slate-900/60" aria-hidden="true" />
            <div className="fixed inset-0 z-[210] flex items-center justify-center p-4">
                <div className="pointer-events-auto max-h-full w-full max-w-lg overflow-y-auto rounded-lg bg-white px-4 pb-4 pt-5 shadow-xl sm:p-6">
                    <h2 id={titleId} className="sr-only">Semiannual Client Review</h2>
                    {children}
                </div>
            </div>
        </FocusTrap>
    );
};

const ScrollableReviewDialog = (props) => (
    <Portal>
        <ScrollableReviewDialogContent {...props} />
    </Portal>
);

export default ScrollableReviewDialog;
